import { createContext, useContext, useMemo, useState } from 'react';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { ApiError, del, get, post, put, type Repo, type Revision } from '../../services/api';
import { useAuth } from '../../hooks/AuthContext';
import { buildPageIndex, type PageIndex } from '../../components/Markdown';
import { ErrorBox, Loading, VisibilityBadge } from '../../components/Ui';
import { useAsync, useTitle } from '../../hooks';
import { NotFound } from '../workspace/NotFound';
import { StarButton } from '../../components/StarButton';
import { Dialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { WIKI_NAME_PATTERN } from '../../shared/utils/validation';
import { WikiPageTree } from '../../components/wiki/WikiPageTree';
import { canWrite } from '../../shared/utils/conversations';

export interface RepoContext {
  repo: Repo;
  /** `/owner/name`, the prefix of every address under this repository. */
  base: string;
  reload: () => void;
}

export const useRepo = () => useOutletContext<RepoContext>();

/** The files of a revision, and an index for resolving `[[wikilinks]]` among them. */
interface ManifestResult {
  revision?: Revision;
  index: PageIndex | null;
  loading: boolean;
  error?: ApiError;
  reload: () => void;
}
const ManifestContext = createContext<{
  repoId: string;
  rev: string | null | undefined;
  result: ManifestResult;
} | null>(null);
export function useManifest(repoId: string, rev: string | null | undefined): ManifestResult {
  const cached = useContext(ManifestContext);
  const matches = cached?.repoId === repoId && cached.rev === rev;
  const own = useLoadedManifest(repoId, matches ? null : rev);
  return matches ? cached.result : own;
}
function useLoadedManifest(repoId: string, rev: string | null | undefined): ManifestResult {
  const revision = useAsync(
    () =>
      rev
        ? get<Revision>(`/repositories/${repoId}/revisions/${encodeURIComponent(rev)}`)
        : Promise.resolve(undefined),
    [repoId, rev],
  );
  const manifest = revision.data?.manifest;
  const index = useMemo<PageIndex | null>(
    () => (manifest ? buildPageIndex(manifest.map((e) => e.path)) : null),
    [manifest],
  );
  return {
    revision: revision.data,
    index,
    loading: revision.loading,
    error: revision.error,
    reload: revision.reload,
  };
}

export function RepoLayout() {
  const { owner = '', repo: name = '' } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const result = useAsync(
    () => get<Repo>(`/users/${encodeURIComponent(owner)}/repositories/${encodeURIComponent(name)}`),
    [owner, name, user?.username],
  );
  useTitle(`${owner}/${name}`);

  if (result.error?.status === 404) return <NotFound what="wiki" />;
  if (result.error)
    return (
      <div className="container">
        <ErrorBox error={result.error} retry={result.reload} />
      </div>
    );
  if (!result.data)
    return (
      <div className="container">
        <Loading />
      </div>
    );

  return <RepoWorkspace repo={result.data} reload={result.reload} />;
}

function RepoWorkspace({ repo, reload }: { repo: Repo; reload: () => void }) {
  const location = useLocation();
  const [params] = useSearchParams();
  const rev = params.get('rev');
  const manifest = useLoadedManifest(repo.id, rev ?? repo.head);
  let path = location.pathname.split('/blob/')[1] ?? '';
  try { path = decodeURIComponent(path); } catch { /* Malformed URL stays inert in navigation. */ }
  const base = `/${repo.owner.username}/${repo.name}`;
  const filesView = location.pathname.startsWith(`${base}/tree/`);
  const canAdmin = repo.role === 'admin' || repo.role === 'owner';

  return (
    <ManifestContext.Provider value={{ repoId: repo.id, rev: rev ?? repo.head, result: manifest }}>
      <div className="repository-workspace">
        <aside className="repository-sidebar">
          <div className="repository-sidebar-title">
            <Icon name="book" />
            <strong>{repo.name}</strong>
          </div>
          <div className="repository-view-tabs">
            <Link to={base} aria-current={!filesView ? 'page' : undefined}>Knowledge</Link>
            <Link to={base + '/tree/'} aria-current={filesView ? 'page' : undefined}>Files</Link>
          </div>
          <WikiPageTree
            revision={manifest.revision}
            base={base}
            rev={rev}
            path={path}
            unavailable={!!manifest.error}
            loading={manifest.loading}
          />
          <nav className="wiki-section-navigation" aria-label="Wiki sections">
            <Link
              to={base}
              aria-current={
                location.pathname === base ||
                location.pathname.startsWith(`${base}/tree/`) ||
                location.pathname.startsWith(`${base}/blob/`)
                  ? 'page'
                  : undefined
              }
            >
              <Icon name="book" />
              Pages
            </Link>
            <Link
              to={`${base}/commits`}
              aria-current={
                location.pathname.includes('/commits') || location.pathname.includes('/commit/')
                  ? 'page'
                  : undefined
              }
            >
              <Icon name="history" />
              History
            </Link>
            <Link
              to={`${base}/issues`}
              aria-current={location.pathname.startsWith(`${base}/issues`) ? 'page' : undefined}
            >
              <Icon name="issue" />
              Issues
            </Link>
            <Link
              to={`${base}/proposals`}
              aria-current={location.pathname.startsWith(`${base}/proposals`) ? 'page' : undefined}
            >
              <Icon name="proposal" />
              Proposals
            </Link>
            <Link
              to={`${base}/discussions`}
              aria-current={location.pathname.startsWith(`${base}/discussions`) ? 'page' : undefined}
            >
              <Icon name="chat" />
              Discussions
            </Link>
            <Link
              to={`${base}/forks`}
              aria-current={location.pathname.endsWith('/forks') ? 'page' : undefined}
            >
              <Icon name="fork" />
              Forks
            </Link>
            {canAdmin && (
              <Link
                to={`${base}/settings`}
                aria-current={location.pathname.endsWith('/settings') ? 'page' : undefined}
              >
                <Icon name="settings" />
                Settings
              </Link>
            )}
          </nav>
        </aside>
        <div className="repository-main">
          <div className="repo-head">
            <Icon name="book" />
            <h1>
              <Link to={`/user/${repo.owner.username}`}>{repo.owner.username}</Link>
              <span className="repo-title-sep"> / </span>
              <strong>
                <Link to={base}>{repo.name}</Link>
              </strong>
            </h1>
            <VisibilityBadge visibility={repo.visibility} />
            <div className="right row" style={{ gap: 8 }}>
              <StarButton repo={repo} onChange={reload} />
              <RepositoryEngagement key={`watch-${repo.id}`} repo={repo} />
              <ForkButton key={`fork-${repo.id}`} repo={repo} />
            </div>
          </div>
          {repo.forkedFrom && (
            <div className="muted row fork-origin" style={{ marginTop: 4 }}>
              <span>
                forked from <Link to={`/${repo.forkedFrom.fullName}`}>{repo.forkedFrom.fullName}</Link>
              </span>
              {canWrite(repo.role) && (
                <>
                  <Link
                    className="btn sm"
                    to={`/${repo.forkedFrom.fullName}/proposals/new?from=${encodeURIComponent(repo.id)}`}
                  >
                    <Icon name="proposal" />
                    Propose changes
                  </Link>
                  <Link
                    className="btn sm"
                    to={`${base}/proposals/new?from=${encodeURIComponent(repo.forkedFrom.id)}`}
                  >
                    <Icon name="refresh" />
                    Update from {repo.forkedFrom.fullName}
                  </Link>
                </>
              )}
            </div>
          )}

          <Outlet key={repo.id} context={{ repo, base, reload } satisfies RepoContext} />
        </div>
      </div>
    </ManifestContext.Provider>
  );
}

function RepositoryEngagement({ repo }: { repo: Repo }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError>();
  const subscription = useAsync(
    () =>
      user
        ? get<{ level: 'none' | 'revisions' }>(`/repositories/${repo.id}/subscription`)
        : Promise.resolve({ level: 'none' as const }),
    [repo.id, user?.id],
  );
  const toggleSubscription = async () => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/${repo.fullName}`)}`);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      if (subscription.data?.level === 'revisions') await del(`/repositories/${repo.id}/subscription`);
      else
        await put(`/repositories/${repo.id}/subscription`, {
          level: 'revisions',
        });
      subscription.reload();
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause : new ApiError(0, 'error', 'Could not update this subscription.'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        className="btn sm"
        disabled={busy || (!!user && (subscription.loading || !!subscription.error))}
        aria-pressed={subscription.data?.level === 'revisions'}
        onClick={() => void toggleSubscription()}
        title="Receive notifications for new synced revisions"
      >
        <Icon name="bell" />
        {subscription.data?.level === 'revisions' ? 'Watching' : 'Watch'}
      </button>
      {subscription.error && <ErrorBox error={subscription.error} retry={subscription.reload} />}
      {error && <ErrorBox error={error} />}
    </>
  );
}

function ForkButton({ repo }: { repo: Repo }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(repo.name);
  // A copy of a public repository is not published by default: the person chooses.
  const [visibility, setVisibility] = useState<'private' | 'public'>('private');
  const [error, setError] = useState<ApiError>();
  const [busy, setBusy] = useState(false);

  const label = `Fork${repo.forks ? ` · ${repo.forks}` : ''}`;
  if (!user) {
    return (
      <Link className="btn" to={`/login?next=${encodeURIComponent(`/${repo.fullName}`)}`}>
        {label}
      </Link>
    );
  }

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const fork = await post<Repo>(`/repositories/${repo.id}/forks`, {
        name: name.trim(),
        visibility,
      });
      navigate(`/${fork.owner.username}/${fork.name}`);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, 'error', 'Something went wrong.'));
      setBusy(false);
    }
  };

  return (
    <div className="menu">
      <button className="btn" aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen(!open)}>
        <Icon name="fork" />
        {label}
      </button>
      {open && (
        <Dialog title="Fork this wiki" busy={busy} onClose={() => setOpen(false)}>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <div className="field" style={{ marginBottom: 10 }}>
              <label htmlFor="fork-name">Name for your copy</label>
              <input
                id="fork-name"
                className="input"
                required
                maxLength={100}
                pattern={WIKI_NAME_PATTERN}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <span className="hint">
                It will live at {user.username}/{name || '…'}.
              </span>
            </div>
            <div className="field" style={{ marginBottom: 10 }}>
              <label htmlFor="fork-visibility">Who can see your copy</label>
              <select
                id="fork-visibility"
                className="input"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as 'private' | 'public')}
              >
                <option value="private">Only me and people I add</option>
                <option value="public">Anyone</option>
              </select>
              <span className="hint">
                Your copy starts from the current files; the history is not copied.
              </span>
            </div>
            {error && <ErrorBox error={error} />}
            <div className="row">
              <button className="btn primary" disabled={busy || !name.trim()} type="submit">
                {busy ? 'Forking…' : 'Create fork'}
              </button>
              <button type="button" className="btn ghost" disabled={busy} onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </div>
  );
}
